import type { PressEvent } from 'react-aria-components';
import { msg } from '@gonfalon/messages/msg';
import { Button, ButtonGroup, IconButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { DataTableCellButton } from './types';

import styles from './DataTable.module.css';

type Props<T> = {
  buttons: Array<DataTableCellButton<T>>;
  row: T;
  rowLabel: string;
};

const releasePointerFocus = (event: PressEvent) => {
  if (event.pointerType !== 'keyboard' && event.pointerType !== 'virtual' && event.target instanceof HTMLElement) {
    event.target.blur();
  }
};

export function DataTableCellButtons<T>({ buttons, row, rowLabel }: Props<T>) {
  if (buttons.length === 0) {
    return null;
  }

  return (
    <ButtonGroup className={styles.cellButtons}>
      {buttons.map((button) => {
        const onPress = (event: PressEvent) => {
          releasePointerFocus(event);
          button.onAction(row);
        };

        if (button.label === undefined) {
          return (
            <IconButton
              key={button.id}
              icon={button.icon}
              size="small"
              variant={button.variant}
              isDisabled={button.isDisabled}
              aria-label={button.ariaLabel}
              onClick={(event) => event.stopPropagation()}
              onPress={onPress}
            />
          );
        }

        return (
          <Button
            key={button.id}
            size="small"
            variant={button.variant}
            isDisabled={button.isDisabled}
            aria-label={
              button.ariaLabel ?? msg('ui.dataTable.cellButtons.actionLabel', { label: button.label, rowLabel })
            }
            onClick={(event) => event.stopPropagation()}
            onPress={onPress}
          >
            {button.icon ? (
              <span className={styles.cellButtonContent}>
                <Icon name={button.icon} size="small" />
                {button.label}
              </span>
            ) : (
              button.label
            )}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
